import { Component } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute } from "@angular/router";
import { AlertService } from "@modules/alert";
import { finalize, forkJoin, take } from "rxjs";
import { LoadingTypeEnum } from "src/app/modules/shared/_enums/loading-type.enum";
import { ConfirmActionModalComponent } from "../../confirm-action-modal/confirm-action-modal/confirm-action-modal.component";
import { BreadcrumbService } from "../../../../_services/breadcrumb.service";
import { ClientsService } from "../../../../_services/clients.service";
import {
  FundingRequestActionData,
  FundingRequestData,
  FundingRequestsService,
} from "../../../../_services/funding-requests.service";

@Component({
  selector: "app-client-funding-request",
  templateUrl: "./client-funding-request.component.html",
  styleUrls: ["./client-funding-request.component.css"],
})
export class ClientFundingRequestComponent {
  isLoadingGeneral = false;
  isUpdatingStatus = false;
  clientId: number;
  fundingRequestId: number;
  fundingRequest: FundingRequestData | null = null;

  constructor(
    private route: ActivatedRoute,
    private fundingRequestsService: FundingRequestsService,
    private clientsService: ClientsService,
    private breadcrumbService: BreadcrumbService,
    private alertService: AlertService,
    private dialog: MatDialog
  ) {
    this.clientId = Number(this.route.parent?.snapshot.params.id);

    this.route.params.subscribe((params) => {
      if (params.fundingRequestId) {
        this.fundingRequestId = Number(params.fundingRequestId);
        this.getFundingRequest();
      }
    });
  }

  getFundingRequest(): void {
    this.isLoadingGeneral = true;

    forkJoin({
      client: this.clientsService.getClient(this.clientId).pipe(take(1)),
      fundingRequest: this.fundingRequestsService
        .getClientFundingRequest(this.clientId, this.fundingRequestId)
        .pipe(take(1)),
    })
      .pipe(finalize(() => (this.isLoadingGeneral = false)))
      .subscribe(
        ({ client, fundingRequest }) => {
          this.fundingRequest = fundingRequest.result;
          this.breadcrumbService.changeBreadcrumbs(
            this.breadcrumbService.setForClient(
              client.result,
              `funding request #${this.fundingRequestId}`
            )
          );
        },
        (error) => {
          if (error?.error?.errors) {
            this.alertService.errors(error.error.errors);
          }
        }
      );
  }

  markAsFunded(): void {
    const action = this.getAvailableAction("fund");

    if (!action || this.isUpdatingStatus) {
      return;
    }

    this.dialog
      .open(ConfirmActionModalComponent, {
        data: {
          action: {
            text: action.label,
            needs_confirmation: true,
            needs_reason: false,
          },
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (!result) {
          return;
        }

        this.isUpdatingStatus = true;

        this.fundingRequestsService
          .markClientFundingRequestAsFunded(
            this.clientId,
            this.fundingRequestId
          )
          .pipe(finalize(() => (this.isUpdatingStatus = false)))
          .subscribe({
            next: () => {
              this.alertService.success("Funding request marked as funded.");
              this.getFundingRequest();
            },
            error: (error) => {
              if (error?.error?.errors) {
                this.alertService.errors(error.error.errors);
              }
            },
          });
      });
  }

  getAvailableAction(actionName: string): FundingRequestActionData | undefined {
    return this.fundingRequest?.available_actions?.find(
      (action) => action.action === actionName
    );
  }

  get LoadingType() {
    return LoadingTypeEnum;
  }
}
